import { inject, injectable, injectFromBase } from 'inversify'
import DomainBaseService from '@/services/domain/domainBaseService'
import { SERVICE_TYPES } from '@/core/container/types'
import { DomainSettings } from '@/decorators/domainDecorators'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import SubAccountPermissions, {
  type DisabledOption,
  type SubAccountPermissionsActions,
  type SubAccountPermissionsContacts,
  type SubAccountPermissionsMessaging,
  type SubAccountPermissionsReporting,
  type SubAccountPermissionsServices,
} from '@/data/models/domain/SubAccountPermissions'
import type SubAccountPermissionsRepository from '@/data/repositories/domain/SubAccountPermissionsRepository'
import type { UpdatePermissionsRequestBody } from '@/data/repositories/domain/SubAccountPermissionsRepository'
import type { ModelRaw } from '@/types'
import { ModelEventType } from '@/services/transport/types'
import TmLogicError from '@/core/error/tmLogicError'

type PermissionsList = keyof (
  | SubAccountPermissionsActions
  | SubAccountPermissionsMessaging
  | SubAccountPermissionsContacts
  | SubAccountPermissionsServices
  | SubAccountPermissionsReporting
)

@DomainSettings({
  model: SubAccountPermissions,
})
@injectable()
@injectFromBase()
export default class SubAccountsPermissionsService extends DomainBaseService<SubAccountPermissionsRepository> {
  constructor(
    @inject(SERVICE_TYPES.EntityManager) entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) preloaderManager: PreloaderManager,
  ) {
    super(entityManager, subscription, preloaderManager)
  }

  public getPermissions(userId: string) {
    return this.getDomainRepository().getPermissions(userId)
  }

  public findUserPermissionsInAnyGroupByName(userId: string, name: keyof PermissionsList) {
    const entity = this.findEntityByIdOrFail(userId)
    for (const entityKey in entity) {
      const entityValue = entity[entityKey]
      if (typeof entityValue === 'object' && entityValue[name]) {
        return entityValue[name] as DisabledOption<boolean>
      }
    }
    throw new TmLogicError(`Not found permission with name "${name.toString()}"`)
  }

  public updatePermissions(userId: string, settings: UpdatePermissionsRequestBody) {
    return this.getDomainRepository().updatePermissions(userId, settings)
  }

  public async updatePermissionsInStore(userId: string, permissions: ModelRaw<SubAccountPermissions>) {
    const result = await this.getDomainRepository().updatePermissionsInStore(userId, permissions)
    this.notify([userId], ModelEventType.UPDATE)
    return result
  }
}
